import { $isDashList, $setDashList } from "@cohesive/ui-components";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  type ListNode,
} from "@lexical/list";
import {
  $findMatchingParent,
  $getSelection,
  $isRangeSelection,
  type LexicalEditor,
} from "lexical";
import { selectFirstRow, withCanvasSelection } from "./canvasFormatCommands";

export type CanvasListType = "bullet" | "dash" | "numbered";

const listTypeOf = (type: CanvasListType) =>
  type === "numbered" ? "number" : "bullet";

function $listAtSelection(): ListNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const list = $findMatchingParent(selection.anchor.getNode(), $isListNode);
  return $isListNode(list) ? list : null;
}

/**
 * Applies a list style to the row the caret is in, or to the first row when
 * there is no caret — one click, no text selection required. Picking the style
 * a row already has takes it off again.
 */
export function applyCanvasListType(
  editor: LexicalEditor | null,
  type: CanvasListType,
): void {
  if (!editor) return;

  withCanvasSelection(editor, selectFirstRow, () => {
    let alreadyApplied = false;
    editor.getEditorState().read(() => {
      const list = $listAtSelection();
      if (!list || list.getListType() !== listTypeOf(type)) return;
      alreadyApplied =
        list.getListType() !== "bullet" ||
        $isDashList(list) === (type === "dash");
    });

    if (alreadyApplied) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(
      type === "numbered"
        ? INSERT_ORDERED_LIST_COMMAND
        : INSERT_UNORDERED_LIST_COMMAND,
      undefined,
    );
    if (type === "numbered") return;
    editor.update(() => {
      const list = $listAtSelection();
      if (list) $setDashList(list, type === "dash");
    });
  });
}
